import API from "./API";
import BaseService from "./BaseService";

const PEXELS_PHOTOS_PATH = "pexels/photos";

export const PEXELS_PAGE_SIZE = 30;

/** The platform API silently clamps anything larger than this. */
export const PEXELS_MAX_PAGE_SIZE = 80;

/** Only valid alongside a query; the endpoint 400s otherwise. */
export const PEXELS_ORIENTATIONS = Object.freeze({
  ALL: "",
  LANDSCAPE: "landscape",
  PORTRAIT: "portrait",
  SQUARE: "square",
});

/**
 * @typedef {import("@/types/data/Pexels.mjs").PexelsPhoto} PexelsPhoto
 * @typedef {import("@/types/data/Pexels.mjs").PexelsPage} PexelsPage
 * @typedef {import("@/types/data/Pexels.mjs").PexelsOrientation} PexelsOrientation
 * @typedef {import("@/types/data/Pexels.mjs").PexelsError} PexelsError
 */

/**
 * @param {Object} raw
 * @returns {PexelsPhoto}
 */
export const normalizePexelsPhoto = (raw) => ({
  id: raw.id,
  width: raw.width,
  height: raw.height,
  url: raw.url,
  alt: raw.alt || "",
  avgColor: raw.avg_color || "",
  photographer: raw.photographer || "",
  photographerUrl: raw.photographer_url || "",
  original: raw.src?.original,
  thumbnail: raw.src?.medium || raw.src?.original,
});

/**
 * @param {Object} body
 * @param {number} perPage
 * @returns {PexelsPage}
 */
const normalizePage = (body, perPage) => {
  const photos = (body.photos || []).map(normalizePexelsPhoto);
  const page = body.page ?? 1;

  // next_page's URL points at Pexels and would not carry our auth, so we page by
  // number. The length check only covers a proxy that strips the cursor.
  const totalResults = body.total_results ?? 0;
  const hasNextPage = body.next_page
    ? true
    : photos.length >= perPage && (!totalResults || page * perPage < totalResults);

  return {
    photos,
    page,
    totalResults,
    nextPage: hasNextPage ? page + 1 : undefined,
  };
};

/**
 * @param {PexelsError} [error]
 * @returns {string}
 */
export const getPexelsErrorMessage = (error) => {
  // Only the 429 message is written for end users; a 400 is a stringified zod blob.
  if (error?.status === 429) return error.message;
  if (error?.status === 400) return "That search couldn't be run. Try a different search.";

  const generic = "Couldn't load photos from Pexels. Try again in a moment.";
  return error?.request ? `${generic} (reference ${error.request})` : generic;
};

/**
 * Stock photo search, proxied by the platform API so the Pexels key stays server-side.
 */
class PexelsService extends BaseService {
  /**
   * @param {{query?: string, page?: number, orientation?: PexelsOrientation, perPage?: number}} [params]
   * @returns {Promise<PexelsPage>}
   */
  async fetchPhotos({ query, page = 1, orientation, perPage = PEXELS_PAGE_SIZE } = {}) {
    const search = query?.trim();
    // Match the server's clamp so normalizePage compares against what we get back.
    const requestedPerPage = Math.min(perPage, PEXELS_MAX_PAGE_SIZE);
    const params = { page, per_page: requestedPerPage };

    if (search) {
      params.query = search;
      // Pexels rejects orientation on curated requests.
      if (orientation) {
        params.orientation = orientation;
      }
    }

    const body = await API.get(PEXELS_PHOTOS_PATH, params, this.getToken());

    return normalizePage(body, requestedPerPage);
  }
}

export default new PexelsService();
